import DefaultButton from '@/components/common/default/DefaultButton';
import Image from 'next/image';
import {
  ImagesSkusFiltersSvg,
  ImagesSkusGridSvg,
  ImagesSkusListSvg,
} from '@/assets/images_skus.assets';
import { SkusMobileAlgoliaFiltersProps } from '@/types/props/components/skus';
import SkusMobileAlgoliaSortBy from './SkusMobileAlgoliaSortBy';
import { useClearRefinements, useCurrentRefinements, useInstantSearch } from 'react-instantsearch';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkusEnum } from '@/types/translations/SkusEnum';
import { usePathname } from 'next/navigation';

export default function SkusMobileAlgoliaFilters({
  viewMode,
  setViewMode,
  allSortsCustomTranslated,
  setFiltersAreOpen,
}: SkusMobileAlgoliaFiltersProps) {
  const pathname = usePathname();
  const { results } = useInstantSearch();
  const { translate } = useTypedTranslation<SkusEnum>('skus');
  const { items } = useCurrentRefinements();
  const { refine: clearAllFilters } = useClearRefinements();

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between">
        <DefaultButton
          className="tw-rounded-full tw-bg-main tw-px-8 tw-py-3 tw-font-plusJakartaSans tw-font-semibold tw-text-white hover:tw-bg-main-shades-700 hover:tw-shadow-md focus:tw-outline focus:tw-outline-2 focus:tw-outline-main-shades-300 active:tw-scale-95 active:tw-bg-main-shades-800 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-400"
          onClick={() => setFiltersAreOpen(true)}
        >
          <Image src={ImagesSkusFiltersSvg} width={24} height={14.4} alt="filters" />
          <span className="tw-ml-2">
            {items.length > 0 ? `Filter (${items.length})` : 'Filter'}
          </span>
        </DefaultButton>
        <DefaultButton
          className="active:tw-text-main-dark focus:tw-outline-main-light tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-text hover:tw-text-main hover:tw-underline focus:tw-outline focus:tw-outline-2 active:tw-scale-95 disabled:tw-cursor-not-allowed disabled:tw-text-gray-400"
          onClick={clearAllFilters}
        >
          {translate(SkusEnum['clear_all_filters'])}
        </DefaultButton>
      </div>
      <div className="tw-flex tw-items-center tw-justify-between tw-pt-8">
        <p className="tw-m-0 tw-p-0 tw-font-plusJakartaSans tw-text-sm tw-font-semibold tw-text-text">
          {pathname?.split('/').pop() !== 'search' &&
            decodeURIComponent(pathname?.split('/').pop() ?? '').replace(/-/g, ' ')}{' '}
          (
          {translate(SkusEnum['numberVariable_results']).replace(
            '{{count}}',
            results.nbHits.toString()
          )}
          )
        </p>
        <div className="tw-flex tw-items-center tw-gap-2">
          <SkusMobileAlgoliaSortBy allSortsCustomTranslated={allSortsCustomTranslated} />

          <DefaultButton
            className="focus:tw-outline-main-light tw-rounded-full tw-p-2 tw-text-text hover:tw-bg-gray-200 hover:tw-opacity-80 focus:tw-outline focus:tw-outline-2 active:tw-scale-90 active:tw-bg-gray-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            aria-label={
              viewMode === 'grid'
                ? translate(SkusEnum['switch_to_list_view'])
                : translate(SkusEnum['switch_to_grid_view'])
            }
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          >
            <Image
              src={viewMode === 'grid' ? ImagesSkusListSvg : ImagesSkusGridSvg}
              width={24}
              height={24}
              alt="grid"
            />
          </DefaultButton>
        </div>
      </div>
    </div>
  );
}
